export const META_PIXEL_ID = "770630797842347";
export const META_CHECKOUT_ID_STORAGE_KEY = "mfs_meta_checkout_id";
export const META_ADD_TO_CART_STORAGE_PREFIX = "mfs_meta_atc_sent_";
export const META_INITIATE_CHECKOUT_STORAGE_PREFIX = "mfs_meta_ic_sent_";
export const META_PURCHASE_STORAGE_PREFIX = "mfs_meta_purchase_sent_";
export const META_LEAD_ID_STORAGE_KEY = "mfs_meta_lead_id";
const META_LEAD_SCOPE_STORAGE_KEY = "mfs_meta_lead_scope";

const ATTRIBUTION_STORAGE_KEY = "mfs_meta_attribution";
const ATTRIBUTION_SESSION_STORAGE_KEY = "mfs_meta_attribution_session";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
// Substring needles: a query key is stripped if its lowercased name contains
// any of these (so access_token, payment_token, reset_token, signature, ... all
// match). Covers token + PII per SE action list (token/email/phone/auth/session/sig/secret).
const BLOCKED_META_URL_QUERY_KEYS = [
    "token",
    "email",
    "phone",
    "auth",
    "session",
    "sig",
    "secret",
    "password",
    "otp",
];
const META_ADVANCED_MATCHING_KEYS = ["em", "ph", "fn", "external_id", "country"];
const META_PIXEL_BUSINESS_CONTEXT_KEYS = [
    "page_url",
    "event_source_url",
    "meta_current_url",
    "meta_current_path",
    "page_source",
    "cms_slug",
    "cms_landing_page_id",
    "original_page_source",
    "funnel_step",
];
const META_PIXEL_PACKAGE_EXTRA_KEYS = [
    ...META_PIXEL_BUSINESS_CONTEXT_KEYS,
    "payment_method",
];
const META_PIXEL_LEAD_EXTRA_KEYS = [
    ...META_PIXEL_BUSINESS_CONTEXT_KEYS,
    "content_type",
];
const META_PIXEL_CONTENT_KEYS = [
    "id",
    "quantity",
    "item_price",
    "product_type",
    "page_source",
    "product_name",
];

let lastAdvancedMatchingKey = "";

const META_BROWSER_EVENT_ENDPOINT = "/api/meta/events";
const META_EVENT_ID_PREFIXES = {
    PageView: "pv",
    ViewContent: "vc",
    Lead: "lead",
    AddToCart: "atc",
    InitiateCheckout: "checkout",
};

const getQueryValue = (value) => {
    if (Array.isArray(value)) {
        return value[0];
    }
    return value;
};

const addIfPresent = (target, key, value) => {
    const normalizedValue = getQueryValue(value);
    if (normalizedValue !== undefined && normalizedValue !== null && normalizedValue !== "") {
        target[key] = normalizedValue;
    }
    return target;
};

const hasMetaParamValue = (value) => {
    if (value === undefined || value === null) {
        return false;
    }
    if (typeof value === "string") {
        return value.trim() !== "";
    }
    if (Array.isArray(value)) {
        return value.length > 0 && value.some((item) => hasMetaParamValue(item));
    }
    if (typeof value === "object") {
        return Object.keys(value).some((key) => hasMetaParamValue(value[key]));
    }
    return true;
};

const pickMetaParams = (source = {}, allowedKeys = []) => allowedKeys.reduce((acc, key) => {
    if (hasMetaParamValue(source[key])) {
        acc[key] = source[key];
    }
    return acc;
}, {});

const mergeMetaParams = (...sources) => sources.reduce((acc, source = {}) => {
    Object.keys(source || {}).forEach((key) => addIfPresent(acc, key, source[key]));
    return acc;
}, {});

const safeJsonStringify = (value) => {
    try {
        return JSON.stringify(value);
    } catch (e) {
        return "";
    }
};

const normalizeHashValue = (value) => String(value || "").trim().toLowerCase();

export const normalizeMetaPhone = (value) => {
    let phone = String(value || "").replace(/[^0-9]/g, "");
    if (!phone) {
        return "";
    }
    if (phone.indexOf("00") === 0) {
        phone = phone.replace(/^00+/, "");
    }
    if (phone.indexOf("0") === 0) {
        return `62${phone.replace(/^0+/, "")}`;
    }
    if (phone.indexOf("8") === 0) {
        return `62${phone}`;
    }
    return phone;
};

export const normalizeMetaCountry = (value) => {
    const rawValue = normalizeHashValue(value);
    if (!rawValue || rawValue === "worldwide" || rawValue === "intl") {
        return "";
    }
    if (rawValue === "indonesia" || rawValue === "idn") {
        return "id";
    }
    const alphaOnly = rawValue.replace(/[^a-z]/g, "");
    if (alphaOnly.length === 2) {
        return alphaOnly;
    }
    return "";
};

export const normalizeMetaCountryCode = (value) => {
    const country = normalizeMetaCountry(value);
    return country ? country.toUpperCase() : "";
};

const META_DUMMY_CLICK_IDS = ["fbclid", "undefined", "null", "none", "nan"];

export const sanitizeMetaClickId = (value) => {
    const normalizedValue = String(getQueryValue(value) || "").trim();
    if (!normalizedValue) {
        return "";
    }
    if (/\s/.test(normalizedValue)) {
        return "";
    }
    if (META_DUMMY_CLICK_IDS.includes(normalizedValue.toLowerCase())) {
        return "";
    }
    return normalizedValue;
};

export const buildMetaFbcFromFbclid = (fbclid, timestamp = Date.now()) => {
    const normalizedFbclid = sanitizeMetaClickId(fbclid);
    if (!normalizedFbclid) {
        return "";
    }
    return `fb.1.${timestamp}.${normalizedFbclid}`;
};

// Meta drops click attribution after 90 days, so an fbc older than that (or a
// malformed / future-dated one) must be dropped on read instead of replayed.
// localStorage never expires, so a stale stored _fbc would otherwise be resent
// forever and surface as the "expired fbclid" warning in Events Manager.
const META_FBC_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const META_FBC_FUTURE_SKEW_MS = 2 * 24 * 60 * 60 * 1000; // tolerate client-clock drift

export const validMetaFbcOrNull = (fbc) => {
    const value = String(fbc || "").trim();
    if (!value) {
        return null;
    }
    // Meta fbc format: fb.<subdomainIndex>.<creationTimeMs>.<fbclid>
    const parts = value.split(".");
    if (parts.length < 4 || parts[0] !== "fb" || !/^\d+$/.test(parts[1])) {
        return null;
    }
    if (!sanitizeMetaClickId(parts.slice(3).join("."))) {
        return null;
    }
    const timestamp = Number(parts[2]);
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
        return null;
    }
    const age = Date.now() - timestamp;
    if (age > META_FBC_MAX_AGE_MS || age < -META_FBC_FUTURE_SKEW_MS) {
        return null;
    }
    return value;
};

export const validMetaFbpOrNull = (fbp) => {
    const value = String(fbp || "").trim();
    if (!value) {
        return null;
    }
    const parts = value.split(".");
    if (parts.length < 4 || parts[0] !== "fb" || !/^\d+$/.test(parts[1]) || !/^\d+$/.test(parts[2])) {
        return null;
    }
    if (!sanitizeMetaClickId(parts.slice(3).join("."))) {
        return null;
    }
    return value;
};

export const buildMetaAdvancedMatching = ({
    email,
    phone,
    firstName,
    externalId,
    userId,
    country,
    countryCode,
} = {}) => {
    const normalizedEmail = normalizeHashValue(email);
    const normalizedPhone = normalizeMetaPhone(phone);
    // fn = first name as a NORMALIZED PLAIN value (trimmed + lowercased). Added so
    // the browser Advanced Matching copy is identity-rich and Meta cannot let a
    // thin (no-first-name) copy win dedup and drop the name from user_data.
    const normalizedFirstName = normalizeHashValue(firstName);
    // external_id = known user id, else the stable anonymous visitor id, so even
    // anonymous top-funnel events (PageView / ViewContent) carry one consistent id.
    const normalizedExternalId = String(externalId || userId || "").trim() || getOrCreateMetaAnonId();
    const normalizedCountry = normalizeMetaCountry(country || countryCode);

    // Browser Pixel Advanced Matching must receive NORMALIZED PLAIN values.
    // Meta's fbevents.js hashes them client-side; pre-hashing here would
    // double-hash (sha256(sha256(x))) and break server-side matching.
    return [
        ["em", normalizedEmail],
        ["ph", normalizedPhone],
        ["fn", normalizedFirstName],
        ["external_id", normalizedExternalId],
        ["country", normalizedCountry],
    ].reduce((acc, [key, value]) => {
        if (value) {
            acc[key] = value;
        }
        return acc;
    }, {});
};

const filterMetaAdvancedMatching = (advancedMatching = {}) => META_ADVANCED_MATCHING_KEYS
    .reduce((acc, key) => addIfPresent(acc, key, advancedMatching[key]), {});

export const applyMetaAdvancedMatching = (advancedMatching = {}) => {
    if (typeof window === "undefined" || !window.fbq || !isMetaPixelEnabled()) {
        return false;
    }
    const payload = filterMetaAdvancedMatching(advancedMatching);
    const payloadKeys = Object.keys(payload);
    if (!payloadKeys.length) {
        return false;
    }
    const payloadKey = JSON.stringify(payloadKeys.sort().reduce((acc, key) => {
        acc[key] = payload[key];
        return acc;
    }, {}));
    if (payloadKey === lastAdvancedMatchingKey) {
        return false;
    }
    window.fbq("init", META_PIXEL_ID, payload);
    lastAdvancedMatchingKey = payloadKey;
    return true;
};

const safeStorage = (type) => {
    if (typeof window === "undefined") return null;
    try {
        return type === "session" ? window.sessionStorage : window.localStorage;
    } catch (e) {
        return null;
    }
};

const getStorageItem = (type, key) => {
    const storage = safeStorage(type);
    if (!storage) return "";
    try {
        return storage.getItem(key) || "";
    } catch (e) {
        return "";
    }
};

const setStorageItem = (type, key, value) => {
    const storage = safeStorage(type);
    if (!storage || !value) return false;
    try {
        storage.setItem(key, value);
        return true;
    } catch (e) {
        return false;
    }
};

const removeStorageItem = (type, key) => {
    const storage = safeStorage(type);
    if (!storage) return false;
    try {
        storage.removeItem(key);
        return true;
    } catch (e) {
        return false;
    }
};

export const hasSentMetaPurchase = (eventID) => {
    const normalizedEventId = String(eventID || "").trim();
    if (!normalizedEventId) {
        return false;
    }
    const key = `${META_PURCHASE_STORAGE_PREFIX}${normalizedEventId}`;
    return !!(getStorageItem("local", key) || getStorageItem("session", key));
};

export const markMetaPurchaseSent = (eventID) => {
    const normalizedEventId = String(eventID || "").trim();
    if (!normalizedEventId) {
        return false;
    }
    const key = `${META_PURCHASE_STORAGE_PREFIX}${normalizedEventId}`;
    const value = String(Date.now());
    const localStored = setStorageItem("local", key, value);
    const sessionStored = setStorageItem("session", key, value);
    return localStored || sessionStored;
};

const parseCookieString = (cookieString = "") => cookieString
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean)
    .reduce((acc, item) => {
        const separatorIndex = item.indexOf("=");
        const rawKey = separatorIndex >= 0 ? item.slice(0, separatorIndex) : item;
        const rawValue = separatorIndex >= 0 ? item.slice(separatorIndex + 1) : "";
        try {
            acc[decodeURIComponent(rawKey)] = decodeURIComponent(rawValue || "");
        } catch (e) {
            acc[rawKey] = rawValue || "";
        }
        return acc;
    }, {});

export const getBrowserCookies = () => {
    if (typeof document === "undefined") {
        return {};
    }
    return parseCookieString(document.cookie || "");
};

// Stable first-party visitor id for anonymous (pre-login) top-funnel events so
// PageView / ViewContent carry an external_id that aligns browser (plain) and
// server (CAPI) for the same event. Becomes the user_id once recognized.
const META_ANON_ID_KEY = "mfs_meta_anon_id";
const META_ANON_ID_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year

const generateMetaUuid = () => {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
    } catch (e) {
        // Fall through to manual id below.
    }
    const rnd = () => Math.random().toString(16).slice(2);
    return `${rnd()}${rnd()}-${Date.now().toString(16)}-${rnd()}`.slice(0, 36);
};

// Cookie domain: share across myfitsociety.com subdomains in prod, host-only on
// localhost/staging so the cookie still writes there.
const metaFirstPartyCookieDomain = () => {
    if (typeof window === "undefined" || !window.location) {
        return "";
    }
    const host = String(window.location.hostname || "").toLowerCase();
    if (host === "myfitsociety.com" || host.endsWith(".myfitsociety.com")) {
        return "; Domain=.myfitsociety.com";
    }
    return "";
};

const writeMetaFirstPartyCookie = (name, value, maxAgeSeconds) => {
    if (typeof document === "undefined" || !value) {
        return;
    }
    try {
        const secure = typeof window !== "undefined" && window.location && window.location.protocol === "https:"
            ? "; Secure"
            : "";
        document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}${metaFirstPartyCookieDomain()}`;
    } catch (e) {
        // Ignore cookie write failures (e.g. privacy mode).
    }
};

export const getOrCreateMetaAnonId = () => {
    if (typeof window === "undefined") {
        return "";
    }
    const cookies = getBrowserCookies();
    let anonId = cookies[META_ANON_ID_KEY]
        || getStorageItem("local", META_ANON_ID_KEY)
        || getStorageItem("session", META_ANON_ID_KEY)
        || "";
    if (!anonId) {
        anonId = generateMetaUuid();
    }
    writeMetaFirstPartyCookie(META_ANON_ID_KEY, anonId, META_ANON_ID_MAX_AGE_SECONDS);
    setStorageItem("local", META_ANON_ID_KEY, anonId);
    setStorageItem("session", META_ANON_ID_KEY, anonId);
    return anonId;
};

// One external_id per event: the recognized user_id when known, else the stable
// anonymous visitor id. Never an order/checkout id.
export const getMetaExternalId = (userId = "") => {
    const normalized = String(userId || "").trim();
    return normalized || getOrCreateMetaAnonId();
};

export const sanitizeMetaUrl = (url) => {
    const rawUrl = String(url || "").trim();
    if (!rawUrl) {
        return "";
    }
    try {
        const base = typeof window !== "undefined" && window.location && window.location.origin
            ? window.location.origin
            : "https://myfitsociety.com";
        const parsedUrl = new URL(rawUrl, base);
        // Drop any query param whose key carries a token/PII (substring match so
        // access_token, payment_token, reset_token, signature, ... are all caught).
        Array.from(parsedUrl.searchParams.keys()).forEach((key) => {
            const lowerKey = String(key || "").toLowerCase();
            if (BLOCKED_META_URL_QUERY_KEYS.some((needle) => lowerKey.includes(needle))) {
                parsedUrl.searchParams.delete(key);
            }
        });
        // Mask long opaque path segments (payment tokens, signed ids) so no secret
        // leaks to Meta/log via the URL path. Real routing is untouched — only the
        // value sent to Meta/log is sanitized.
        parsedUrl.pathname = parsedUrl.pathname
            .split("/")
            .map((segment) => (segment.length > 24 ? ":id" : segment))
            .join("/");
        return parsedUrl.toString();
    } catch (e) {
        return rawUrl;
    }
};

const getCurrentUrl = () => {
    if (typeof window === "undefined" || !window.location) {
        return "";
    }
    return sanitizeMetaUrl(window.location.href || "");
};

const getCurrentPath = () => {
    const currentUrl = getCurrentUrl();
    if (currentUrl) {
        try {
            return new URL(currentUrl, typeof window !== "undefined" && window.location ? window.location.origin : "https://myfitsociety.com").pathname;
        } catch (e) {
            // Fall through to direct location path.
        }
    }
    if (typeof window === "undefined" || !window.location) return "";
    return window.location.pathname || "";
};

const normalizePageViewDedupPath = (pathOrUrl = "") => {
    let pathname = String(pathOrUrl || "").trim();
    if (!pathname) {
        return "";
    }
    try {
        const base = typeof window !== "undefined" && window.location && window.location.origin
            ? window.location.origin
            : "https://myfitsociety.com";
        pathname = new URL(pathname, base).pathname;
    } catch (e) {
        pathname = pathname.split("?")[0].split("#")[0];
    }
    pathname = pathname.replace(/\/+$/, "") || "/";
    return pathname.replace(/^\/id(?=\/|$)/, "") || "/";
};

const getPageViewDedupKey = (params = {}) => normalizePageViewDedupPath(
    params.meta_current_path
    || params.page_path
    || params.meta_current_url
    || params.page_url
    || getCurrentPath()
    || getCurrentUrl()
);

const getCurrentSearch = () => {
    const currentUrl = getCurrentUrl();
    if (currentUrl) {
        try {
            return new URL(currentUrl, typeof window !== "undefined" && window.location ? window.location.origin : "https://myfitsociety.com").search;
        } catch (e) {
            // Fall through to direct location search.
        }
    }
    if (typeof window === "undefined" || !window.location) return "";
    return window.location.search || "";
};

const getUrlSearchParams = (url) => {
    try {
        if (url) {
            const base = typeof window !== "undefined" && window.location ? window.location.origin : "https://myfitsociety.com";
            return new URL(sanitizeMetaUrl(url), base).searchParams;
        }
        if (typeof window !== "undefined" && window.location) {
            return new URLSearchParams(getCurrentSearch());
        }
    } catch (e) {
        return new URLSearchParams();
    }
    return new URLSearchParams();
};

export const getMetaUrlContext = (url) => {
    if (typeof window === "undefined") {
        return {};
    }
    const href = sanitizeMetaUrl(url || getCurrentUrl());
    let pathname = getCurrentPath();
    let search = getCurrentSearch();
    try {
        const parsedUrl = href ? new URL(href, window.location.origin) : null;
        if (parsedUrl) {
            pathname = parsedUrl.pathname;
            search = parsedUrl.search;
        }
    } catch (e) {
        // Keep the location-derived fallback values.
    }

    return {
        page_url: href,
        event_source_url: href,
        meta_current_url: href,
        meta_current_path: pathname,
        page_path: pathname,
        page_search: search,
        page_title: typeof document !== "undefined" ? document.title || "" : "",
        meta_referrer: typeof document !== "undefined" ? document.referrer || "" : "",
    };
};

// Meta Pixel hanya boleh trigger di domain produksi persis myfitsociety.com.
// Staging (mfsdev.net/prod.mfsdev.net), localhost, dan subdomain lain tidak boleh mengirim pixel.
export const isMetaPixelHost = (host) => {
    if (!host) {
        return false;
    }
    const hostname = `${host}`.split(":")[0].toLowerCase();
    return hostname === "myfitsociety.com";
};

// Client-side gate: cek hostname window aktif.
export const isMetaPixelEnabled = () => {
    if (typeof window === "undefined" || !window.location) {
        return false;
    }
    return isMetaPixelHost(window.location.hostname);
};

// Server-side gate: cek host dari request (getInitialProps).
export const isMetaPixelEnabledForCtx = (ctx) => {
    const host =
        (ctx && ctx.req && ctx.req.headers && ctx.req.headers.host) ||
        (typeof window !== "undefined" && window.location ? window.location.host : "");
    return isMetaPixelHost(host);
};

// Bootstrap fbq + init pixel. Return false (no-op) kalau domain bukan myfitsociety.com.
// Idempotent: kalau fbq sudah ter-bootstrap (mis. oleh loader global di _document),
// JANGAN reset queue / re-init. Cukup pastikan fungsi fbq ada lalu return true.
export const initMetaPixel = () => {
    if (typeof window === "undefined") {
        return false;
    }
    if (!isMetaPixelEnabled()) {
        return false;
    }
    if (window.fbq && window.fbq.loaded) {
        // Sudah di-bootstrap loader global; jangan timpa queue/init lagi.
        return true;
    }
    window.fbq =
        window.fbq ||
        function () {
            window.fbq.callMethod
                ? window.fbq.callMethod.apply(window.fbq, arguments)
                : window.fbq.queue.push(arguments);
        };
    if (!window._fbq) window._fbq = window.fbq;
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = "2.0";
    window.fbq.queue = [];
    window.fbq("init", META_PIXEL_ID);
    return true;
};

const createMetaEventId = (event, params = {}) => {
    if (event === "AddToCart" && params.checkout_id) {
        return `atc_${params.checkout_id}`;
    }
    if (event === "InitiateCheckout" && params.checkout_id) {
        return `checkout_${params.checkout_id}`;
    }
    if (event === "Lead" && params.lead_id) {
        return `lead_${params.lead_id}`;
    }
    const prefix = META_EVENT_ID_PREFIXES[event] || "evt";
    const randomPart = Math.random().toString(36).slice(2, 10);
    return `${prefix}_${Date.now().toString(36)}_${randomPart}`;
};

const getMetaEventId = (event, params = {}, options = {}) => {
    if (options && options.eventID) {
        return options.eventID;
    }
    return createMetaEventId(event, params);
};

const buildMetaBrowserEventParams = (params = {}) => {
    const eventUrl = params.meta_event_source_url
        || params.event_source_url
        || params.meta_current_url
        || params.page_url
        || "";
    const pageMetadata = mergeMetaParams(getMetaUrlContext(eventUrl), params);
    return mergeMetaParams(buildMetaTrackingPayload({ pageMetadata, query: params }), params);
};

// Map the browser identity object (either the Pixel Advanced Matching shape
// em/ph/fn/external_id, or an already-plain contract shape) into the PLAIN
// user_data the server CAPI proxy expects. Values stay plain — the BE resolves
// identity from external_id and hashes email/phone/first_name server-side.
const buildForwardUserData = (identity = {}, customData = {}) => {
    const userData = {};
    addIfPresent(userData, "email", identity.email || identity.em);
    addIfPresent(userData, "phone", identity.phone || identity.ph);
    addIfPresent(userData, "first_name", identity.first_name || identity.fn);
    addIfPresent(userData, "external_id", identity.external_id || identity.externalId);
    addIfPresent(userData, "fbp", identity.fbp || customData._fbp);
    addIfPresent(userData, "fbc", identity.fbc || customData._fbc);
    return userData;
};

export const sendMetaBrowserEvent = (event, params = {}, eventID = "", options = {}) => {
    if (typeof window === "undefined" || !isMetaPixelEnabled() || !event || !eventID) {
        return false;
    }
    // PageView and ViewContent are browser-only standard Pixel events. Never
    // forward a server CAPI copy of either — that duplicate is what Meta was
    // tagging as "Browser Processed + Server Processed" in Test Events.
    if (event === "PageView" || event === "ViewContent") {
        return false;
    }
    const customData = buildMetaBrowserEventParams(params || {});
    // Always forward the FULL sanitized current page href (never the root domain)
    // so Meta attributes the event to the real page, not "/".
    const eventSourceUrl = customData.event_source_url
        || customData.meta_event_source_url
        || customData.meta_current_url
        || customData.page_url
        || getCurrentUrl();
    const identity = options.advancedMatching || options.userData || {};
    const payload = {
        event_name: event,
        event_id: eventID,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: eventSourceUrl,
        action_source: "website",
        user_data: buildForwardUserData(identity, customData),
        custom_data: customData,
        // Legacy mirror kept during the FE↔BE rollout (shared multi-session tree);
        // identical to custom_data so an un-migrated reader still resolves.
        params: customData,
    };
    const body = safeJsonStringify(payload);
    if (!body) {
        return false;
    }

    try {
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
            const sent = navigator.sendBeacon(
                META_BROWSER_EVENT_ENDPOINT,
                new Blob([body], { type: "application/json" })
            );
            if (sent) {
                return true;
            }
        }
    } catch (e) {
        // Fall back to fetch below.
    }

    if (typeof fetch !== "function") {
        return false;
    }
    try {
        fetch(META_BROWSER_EVENT_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
        }).catch(() => {});
        return true;
    } catch (e) {
        return false;
    }
};

// Guarded event senders. Semua no-op kecuali domain produksi + fbq tersedia.
// Dipakai untuk full-funnel tracking di semua page/step tanpa nyebar window.fbq mentah.
export const trackPixel = (event, params, options = {}) => {
    if (typeof window === "undefined" || !window.fbq || !isMetaPixelEnabled()) {
        return false;
    }
    if (event === "Purchase") {
        return false;
    }
    if (options && options.applyAdvancedMatching === true) {
        applyMetaAdvancedMatching(options.advancedMatching || options.userData || {});
    }
    const eventID = getMetaEventId(event, params || {}, options || {});
    const eventOptions = eventID ? { eventID } : null;
    if (params) {
        if (eventOptions) {
            window.fbq("track", event, params, eventOptions);
        } else {
            window.fbq("track", event, params);
        }
    } else if (eventOptions) {
        window.fbq("track", event, {}, eventOptions);
    } else {
        window.fbq("track", event);
    }
    sendMetaBrowserEvent(event, params || {}, eventID, options || {});
    return true;
};

export const trackPixelPurchase = (params = {}, options = {}) => {
    if (typeof window === "undefined" || !window.fbq || !isMetaPixelEnabled()) {
        return false;
    }
    const eventID = String((options && options.eventID) || "").trim();
    if (!eventID) {
        return false;
    }
    if (options && options.applyAdvancedMatching === true) {
        applyMetaAdvancedMatching(options.advancedMatching || options.userData || {});
    }
    window.fbq("track", "Purchase", params || {}, { eventID });
    return true;
};

export const buildPageMetadata = ({ pageProps = {}, router = {}, url } = {}) => {
    const query = router.query || {};
    const source = {
        ...((pageProps && pageProps.additionalProps) || {}),
        ...pageProps,
        ...query,
    };
    const urlContext = getMetaUrlContext(url);
    const pageUrl = urlContext.page_url || "";
    const params = getUrlSearchParams(pageUrl);
    const metadata = {
        ...urlContext,
    };

    ["page_source", "cms_slug", "cms_landing_page_id", "content_type"].forEach((key) => {
        addIfPresent(metadata, key, source[key] || params.get(key));
    });

    return metadata;
};

export const captureMetaAttribution = ({ pageMetadata = {}, query = {} } = {}) => {
    if (typeof window === "undefined") {
        return {};
    }

    const cookies = getBrowserCookies();
    const params = getUrlSearchParams();
    const storedRaw = getStorageItem("local", ATTRIBUTION_STORAGE_KEY);
    const sessionRaw = getStorageItem("session", ATTRIBUTION_SESSION_STORAGE_KEY);
    let stored = {};
    let sessionStored = {};
    try {
        stored = storedRaw ? JSON.parse(storedRaw) : {};
    } catch (e) {
        stored = {};
    }
    try {
        sessionStored = sessionRaw ? JSON.parse(sessionRaw) : {};
    } catch (e) {
        sessionStored = {};
    }

    const urlContext = getMetaUrlContext(pageMetadata.page_url);
    const currentLandingUrl = urlContext.meta_current_url || getCurrentUrl();
    const currentLandingPath = urlContext.meta_current_path || getCurrentPath();
    const currentReferrer = urlContext.meta_referrer || "";
    const firstLandingUrl = sessionStored.first_landing_url || currentLandingUrl;
    const firstLandingPath = sessionStored.first_landing_path || currentLandingPath;
    const firstReferrer = sessionStored.first_referrer || currentReferrer;
    const cfCountry = normalizeMetaCountryCode(cookies.cf_country);
    const paymentCountry = normalizeMetaCountryCode(cookies.payment_country);
    const storedCountry = normalizeMetaCountryCode(stored.meta_country || stored.country_code || stored.country);
    const queryCountry = normalizeMetaCountryCode(query.meta_country || query.country_code || query.country || params.get("country_code") || params.get("country"));
    const metaCountry = queryCountry || paymentCountry || cfCountry || storedCountry;
    const currentFbclid = sanitizeMetaClickId(query.fbclid || params.get("fbclid") || cookies.fbclid);
    const storedLocalFbclid = getStorageItem("local", "fbclid");
    const storedSessionFbclid = getStorageItem("session", "fbclid");
    const storedLocalFbc = getStorageItem("local", "_fbc");
    const storedSessionFbc = getStorageItem("session", "_fbc");
    if (storedLocalFbclid && !sanitizeMetaClickId(storedLocalFbclid)) {
        removeStorageItem("local", "fbclid");
    }
    if (storedSessionFbclid && !sanitizeMetaClickId(storedSessionFbclid)) {
        removeStorageItem("session", "fbclid");
    }
    if (storedLocalFbc && !validMetaFbcOrNull(storedLocalFbc)) {
        removeStorageItem("local", "_fbc");
    }
    if (storedSessionFbc && !validMetaFbcOrNull(storedSessionFbc)) {
        removeStorageItem("session", "_fbc");
    }
    // A new fbclid in the URL always wins with a fresh timestamp; otherwise reuse
    // a still-valid fbc (cookie first, then persisted storage). Any fbc older than
    // 90 days is dropped here so a stale stored _fbc is never replayed.
    const freshFbcFromClick = currentFbclid ? buildMetaFbcFromFbclid(currentFbclid) : "";
    const validCookieFbc = validMetaFbcOrNull(cookies._fbc);
    const fbc = freshFbcFromClick
        || validCookieFbc
        || validMetaFbcOrNull(storedLocalFbc)
        || validMetaFbcOrNull(storedSessionFbc)
        || "";
    if (fbc && !validCookieFbc) {
        setStorageItem("local", "_fbc", fbc);
        setStorageItem("session", "_fbc", fbc);
    }
    // A stale stored _fbc is left untouched but inert: validMetaFbcOrNull() drops
    // it on every read, so it can never be replayed even if storage is not cleared.

    // Durable first-touch snapshot: the FB cookies (_fbp / fbclid) are short-lived
    // (server fbclid cookie maxAge ~1h), so persist them to standalone storage keys
    // — the same keys re-read above — so a later checkout still carries them.
    const fbp = validMetaFbpOrNull(cookies._fbp)
        || validMetaFbpOrNull(getStorageItem("local", "_fbp"))
        || validMetaFbpOrNull(getStorageItem("session", "_fbp"))
        || "";
    if (currentFbclid) {
        setStorageItem("local", "fbclid", currentFbclid);
        setStorageItem("session", "fbclid", currentFbclid);
    }
    if (fbp) {
        setStorageItem("local", "_fbp", fbp);
        setStorageItem("session", "_fbp", fbp);
    }

    const attribution = {
        _fbp: fbp,
        _fbc: fbc,
        fbclid: currentFbclid,
        first_landing_url: firstLandingUrl,
        first_landing_path: firstLandingPath,
        first_referrer: firstReferrer,
        current_landing_url: currentLandingUrl,
        current_landing_path: currentLandingPath,
        current_referrer: currentReferrer,
        client_user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || "",
        client_language: (typeof navigator !== "undefined" && navigator.language) || "",
        client_timezone_offset: new Date().getTimezoneOffset(),
        cf_country: cfCountry,
        country: metaCountry,
        country_code: metaCountry,
        meta_country: metaCountry,
    };

    if (typeof window.screen !== "undefined") {
        attribution.client_screen_width = window.screen.width;
        attribution.client_screen_height = window.screen.height;
    }

    UTM_KEYS.forEach((key) => {
        attribution[key] = query[key] || params.get(key) || stored[key] || "";
    });

    ["page_source", "cms_slug", "original_page_source"].forEach((key) => {
        attribution[key] = pageMetadata[key] || query[key] || params.get(key) || stored[key] || "";
    });

    setStorageItem("local", ATTRIBUTION_STORAGE_KEY, JSON.stringify({
        ...stored,
        ...UTM_KEYS.reduce((acc, key) => addIfPresent(acc, key, attribution[key]), {}),
        page_source: attribution.page_source || stored.page_source || "",
        cms_slug: attribution.cms_slug || stored.cms_slug || "",
        original_page_source: attribution.original_page_source || stored.original_page_source || "",
        meta_country: attribution.meta_country || stored.meta_country || "",
        country_code: attribution.country_code || stored.country_code || "",
        first_landing_url: firstLandingUrl,
        first_landing_path: firstLandingPath,
        first_referrer: firstReferrer,
    }));
    setStorageItem("session", ATTRIBUTION_SESSION_STORAGE_KEY, JSON.stringify({
        first_landing_url: firstLandingUrl,
        first_landing_path: firstLandingPath,
        first_referrer: firstReferrer,
    }));

    return Object.keys(attribution).reduce((acc, key) => addIfPresent(acc, key, attribution[key]), {});
};

export const buildMetaTrackingPayload = ({ pageMetadata = {}, query = {}, checkoutId = "" } = {}) => {
    const attribution = captureMetaAttribution({ pageMetadata, query });
    const payload = {
        ...attribution,
        ...pageMetadata,
    };
    addIfPresent(payload, "checkout_id", checkoutId);
    addIfPresent(payload, "original_page_source", query.original_page_source || pageMetadata.original_page_source);
    addIfPresent(payload, "meta_first_landing_url", attribution.first_landing_url);
    addIfPresent(payload, "meta_first_landing_path", attribution.first_landing_path);
    addIfPresent(payload, "meta_first_referrer", attribution.first_referrer);
    addIfPresent(payload, "meta_current_url", attribution.current_landing_url);
    addIfPresent(payload, "meta_current_path", attribution.current_landing_path);
    addIfPresent(payload, "meta_event_source_url", attribution.current_landing_url);
    addIfPresent(payload, "meta_user_agent", attribution.client_user_agent);
    return payload;
};

export const isValidCheckoutId = (checkoutId) => /^chk_[A-Za-z0-9_-]{8,80}$/.test(String(checkoutId || ""));

export const parseMetaPriceAmount = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    const rawValue = String(value || "").trim();
    if (!rawValue) {
        return undefined;
    }

    let normalizedValue = rawValue.replace(/[^0-9.,-]/g, "");
    if (!normalizedValue) {
        return undefined;
    }

    const lastDotIndex = normalizedValue.lastIndexOf(".");
    const lastCommaIndex = normalizedValue.lastIndexOf(",");
    if (lastDotIndex >= 0 && lastCommaIndex >= 0) {
        if (lastDotIndex > lastCommaIndex) {
            normalizedValue = normalizedValue.replace(/,/g, "");
        } else {
            normalizedValue = normalizedValue.replace(/\./g, "").replace(",", ".");
        }
    } else if (lastCommaIndex >= 0) {
        const decimalLength = normalizedValue.length - lastCommaIndex - 1;
        normalizedValue = decimalLength === 3
            ? normalizedValue.replace(/,/g, "")
            : normalizedValue.replace(",", ".");
    } else if (lastDotIndex >= 0) {
        const decimalLength = normalizedValue.length - lastDotIndex - 1;
        if (decimalLength === 3) {
            normalizedValue = normalizedValue.replace(/\./g, "");
        }
    }

    const amount = Number(normalizedValue);
    return Number.isFinite(amount) && amount >= 0 ? amount : undefined;
};

export const firstMetaPriceAmount = (...values) => {
    for (let index = 0; index < values.length; index += 1) {
        const amount = parseMetaPriceAmount(values[index]);
        if (amount !== undefined) {
            return amount;
        }
    }
    return undefined;
};

export const generateCheckoutId = () => {
    const randomPart = Math.random().toString(36).slice(2, 12);
    const timestampPart = Date.now().toString(36);
    return `chk_${timestampPart}_${randomPart}`;
};

export const storeCheckoutId = (checkoutId) => {
    if (!isValidCheckoutId(checkoutId)) {
        return "";
    }
    setStorageItem("local", META_CHECKOUT_ID_STORAGE_KEY, checkoutId);
    setStorageItem("session", META_CHECKOUT_ID_STORAGE_KEY, checkoutId);
    return checkoutId;
};

export const getStoredCheckoutId = () => {
    const localId = getStorageItem("local", META_CHECKOUT_ID_STORAGE_KEY);
    if (isValidCheckoutId(localId)) {
        return localId;
    }
    const sessionId = getStorageItem("session", META_CHECKOUT_ID_STORAGE_KEY);
    if (isValidCheckoutId(sessionId)) {
        return sessionId;
    }
    return "";
};

export const getOrCreateCheckoutId = (preferredCheckoutId = "") => {
    if (isValidCheckoutId(preferredCheckoutId)) {
        return storeCheckoutId(preferredCheckoutId);
    }
    const storedCheckoutId = getStoredCheckoutId();
    if (storedCheckoutId) {
        return storedCheckoutId;
    }
    return storeCheckoutId(generateCheckoutId());
};

export const createCheckoutId = () => storeCheckoutId(generateCheckoutId());

export const generateLeadId = () => {
    const randomPart = Math.random().toString(36).slice(2, 12);
    const timestampPart = Date.now().toString(36);
    return `ld_${timestampPart}_${randomPart}`;
};

export const storeLeadId = (leadId) => {
    const normalizedLeadId = String(leadId || "").trim();
    if (!normalizedLeadId) {
        return "";
    }
    setStorageItem("local", META_LEAD_ID_STORAGE_KEY, normalizedLeadId);
    setStorageItem("session", META_LEAD_ID_STORAGE_KEY, normalizedLeadId);
    return normalizedLeadId;
};

const storeLeadScope = (scopeKey) => {
    const normalizedScopeKey = String(scopeKey || "").trim();
    if (!normalizedScopeKey) {
        return "";
    }
    setStorageItem("local", META_LEAD_SCOPE_STORAGE_KEY, normalizedScopeKey);
    setStorageItem("session", META_LEAD_SCOPE_STORAGE_KEY, normalizedScopeKey);
    return normalizedScopeKey;
};

const getStoredLeadScope = () => {
    const localScope = getStorageItem("local", META_LEAD_SCOPE_STORAGE_KEY);
    if (localScope) {
        return localScope;
    }
    return getStorageItem("session", META_LEAD_SCOPE_STORAGE_KEY);
};

export const getStoredLeadId = () => {
    const localId = getStorageItem("local", META_LEAD_ID_STORAGE_KEY);
    if (localId) {
        return localId;
    }
    return getStorageItem("session", META_LEAD_ID_STORAGE_KEY);
};

export const getOrCreateLeadId = (preferredLeadId = "", scopeKey = "") => {
    const normalizedLeadId = String(preferredLeadId || "").trim();
    const normalizedScopeKey = String(scopeKey || "").trim();
    if (normalizedLeadId) {
        if (normalizedScopeKey) {
            storeLeadScope(normalizedScopeKey);
        }
        return storeLeadId(normalizedLeadId);
    }
    if (normalizedScopeKey && getStoredLeadScope() !== normalizedScopeKey) {
        storeLeadScope(normalizedScopeKey);
        return storeLeadId(generateLeadId());
    }
    const storedLeadId = getStoredLeadId();
    if (storedLeadId) {
        return storedLeadId;
    }
    if (normalizedScopeKey) {
        storeLeadScope(normalizedScopeKey);
    }
    return storeLeadId(generateLeadId());
};

export const getCheckoutIdFromUrl = (url) => {
    const params = getUrlSearchParams(url);
    const checkoutId = params.get("checkout_id") || "";
    return isValidCheckoutId(checkoutId) ? checkoutId : "";
};

// Trello 546 C008: every caller assigns the result to window.location.href, so
// a javascript:/data:/vbscript: (or any non-http) URL must never come back out
// verbatim — it is replaced by the site root instead. http(s) URLs, including
// other origins, are returned exactly as before.
const SAFE_NAVIGATION_PROTOCOLS = ["http:", "https:"];
export const UNSAFE_NAVIGATION_URL_FALLBACK = "/";

const isUnsafeNavigationUrl = (url, baseOrigin) => {
    if (typeof url !== "string") {
        return false;
    }
    try {
        return SAFE_NAVIGATION_PROTOCOLS.indexOf(new URL(url, baseOrigin).protocol) === -1;
    } catch (e) {
        // The browser's own URL parser rejects it too, so it cannot navigate anywhere.
        return false;
    }
};

export const appendCheckoutIdToUrl = (url, checkoutId = getOrCreateCheckoutId()) => {
    const currentOrigin = typeof window !== "undefined" && window.location ? window.location.origin : "https://myfitsociety.com";
    if (url && isUnsafeNavigationUrl(url, currentOrigin)) {
        return UNSAFE_NAVIGATION_URL_FALLBACK;
    }
    if (!url || !isValidCheckoutId(checkoutId)) {
        return url;
    }
    try {
        const parsedUrl = new URL(url, currentOrigin);
        const isRelativeUrl = url.charAt(0) === "/";
        const isSameOrigin = parsedUrl.origin === currentOrigin;
        if (!isRelativeUrl && !isSameOrigin) {
            return url;
        }
        parsedUrl.searchParams.set("checkout_id", checkoutId);
        if (isRelativeUrl) {
            return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        }
        return parsedUrl.toString();
    } catch (e) {
        return url;
    }
};

const resolvePackagePrice = (pkg = {}) => {
    const priceCandidates = [
        pkg.total_price,
        pkg.final_price,
        pkg.price,
        pkg.amount,
        pkg.flashsale_pricing && pkg.flashsale_pricing.first_price,
    ];
    const numericPrice = priceCandidates.map((value) => Number(value)).find((value) => Number.isFinite(value) && value >= 0);
    return Number.isFinite(numericPrice) ? numericPrice : undefined;
};

export const buildPackageEventParams = ({ content = {}, packageItem = {}, pageMetadata = {}, checkoutId = "", extra = {} } = {}) => {
    const pkg = packageItem && packageItem.id ? packageItem : content || {};
    const packageId = pkg.id || content.id || content.product_package_id || "";
    const normalizedPackageId = packageId ? String(packageId).replace(/__fp$/, "") : "";
    const contentName = pkg.name || pkg.checkout_display_name || content.name || "";
    const productType = pkg.type || content.type || pkg.product_type || content.product_type || "";
    const currency = pkg.currency_code || pkg.currency || content.currency_code || content.currency || "";
    const value = resolvePackagePrice(pkg);
    const eventUrlContext = getMetaUrlContext(pageMetadata.page_url || extra.page_url);
    const businessContext = pickMetaParams({
        ...eventUrlContext,
        ...pageMetadata,
        ...extra,
    }, META_PIXEL_PACKAGE_EXTRA_KEYS);
    const contentParams = pickMetaParams({
        id: normalizedPackageId,
        quantity: 1,
        product_type: productType,
        page_source: businessContext.page_source,
        product_name: contentName,
    }, META_PIXEL_CONTENT_KEYS);
    const params = {
        ...businessContext,
        content_type: "product",
    };
    addIfPresent(params, "content_name", contentName);
    if (normalizedPackageId) {
        params.content_ids = [normalizedPackageId];
    }
    if (normalizedPackageId || contentName || productType || value !== undefined) {
        params.contents = [contentParams];
    }
    if (value !== undefined) {
        params.value = value;
        params.contents = params.contents || [{}];
        params.contents[0].item_price = value;
    }
    addIfPresent(params, "package_uuid", normalizedPackageId);
    addIfPresent(params, "package_duration_months", pkg.validity_period_amount || pkg.package_duration_months);
    addIfPresent(params, "currency", currency);
    addIfPresent(params, "checkout_id", checkoutId);
    return params;
};

export const buildLeadEventParams = ({ pageMetadata = {}, leadId = "", quizSessionId = "", extra = {} } = {}) => {
    const eventUrlContext = getMetaUrlContext(pageMetadata.page_url || extra.page_url);
    const params = {
        ...pickMetaParams({
            ...eventUrlContext,
            ...pageMetadata,
            ...extra,
        }, META_PIXEL_LEAD_EXTRA_KEYS),
        content_name: extra.content_name || pageMetadata.content_name || "Quiz Completed Lead",
    };
    addIfPresent(params, "lead_id", leadId);
    addIfPresent(params, "quiz_session_id", quizSessionId);

    [
        "value",
        "currency",
        "contents",
        "content_ids",
        "num_items",
        "package_uuid",
        "package_duration_months",
        "item_price",
    ].forEach((key) => {
        delete params[key];
    });

    return params;
};

export const buildPageViewEventParams = (params = {}) => {
    const pageMetadata = {
        ...getMetaUrlContext(params.page_url),
        ...params,
    };
    return buildMetaTrackingPayload({ pageMetadata, query: params });
};

// Guard against PageView double-fire: _app fires once on mount AND again on the
// routeChangeComplete a locale redirect emits ~1s later (and React double-mounts).
// Drop a duplicate PageView for the same path fired within a short window so each
// real page view / route change sends exactly one standard PageView.
let lastPageViewKey = "";
let lastPageViewAt = 0;
export const trackPixelPageView = (params = {}, options = {}) => {
    if (typeof window !== "undefined") {
        const key = getPageViewDedupKey(params);
        const now = Date.now();
        if (key && key === lastPageViewKey && (now - lastPageViewAt) < 2000) {
            return false;
        }
        lastPageViewKey = key;
        lastPageViewAt = now;
    }
    return trackPixel("PageView", buildPageViewEventParams(params), options);
};
export const trackPixelViewContent = (params = {}, options = {}) => trackPixel("ViewContent", params, options);
export const trackPixelLead = (params = {}, options = {}) => trackPixel("Lead", params, options);
export const trackPixelAddToCart = (params = {}, options = {}) => trackPixel("AddToCart", params, options);
export const trackPixelInitiateCheckout = (params = {}, options = {}) => trackPixel("InitiateCheckout", params, options);

export const trackPixelViewContentOnce = (storageKey, params = {}, options = {}) => {
    if (typeof window === "undefined") return false;
    try {
        if (storageKey && window.sessionStorage.getItem(storageKey) === "1") {
            return false;
        }
    } catch (e) { /* storage unavailable */ }

    const sent = trackPixelViewContent(params, options);
    if (sent && storageKey) {
        try {
            window.sessionStorage.setItem(storageKey, "1");
        } catch (e) { /* storage unavailable */ }
    }
    return sent;
};

export const saveInvoiceDataToLocalStorage = (invoiceId, data) => {
    localStorage.setItem(`invoice_${invoiceId}`, JSON.stringify(data));
};

export const findInvoiceDataInLocalStorage = (invoiceId) => {
    const data = localStorage.getItem(`invoice_${invoiceId}`);
    return data ? JSON.parse(data) : null;
};
